import React, { useEffect, useRef, useState } from 'react';
import Hls from 'hls.js';
import ReactPlayer from 'react-player';

// Adjust this interface to match your project's VideoPlayerProps
interface EncryptedHLSPlayerProps {
  src: string; // Changed from 'url' to 'src' to match your project's prop name
  onQualityChange?: (quality: string) => void;
  // Include all other props that might be in your VideoPlayerProps
  // For example:
  controls?: boolean;
  width?: string | number;
  height?: string | number;
  playing?: boolean;
  tracks?: Array<{
    kind: string;
    src: string;
    srcLang: string;
    label: string;
    default?: boolean;
  }>;
  // Add any other ReactPlayer props you want to support
  [key: string]: any; // This allows any other props to be passed through
}

const EncryptedHLSPlayer: React.FC<EncryptedHLSPlayerProps> = ({ 
  src, // Using src instead of url
  onQualityChange,
  tracks,
  ...props 
}) => {
  const playerRef = useRef<ReactPlayer | null>(null);
  const hlsRef = useRef<Hls | null>(null);
  const [qualities, setQualities] = useState<Array<{label: string, value: number}>>([]);
  const [currentQuality, setCurrentQuality] = useState<number | null>(null);

  useEffect(() => {
    const video = playerRef.current?.getInternalPlayer() as HTMLVideoElement | null;

    if (video && Hls.isSupported()) {
      if (hlsRef.current) {
        hlsRef.current.destroy();
      }
      
      const hls = new Hls({
        capLevelToPlayerSize: true,
        autoStartLoad: true,
      });
      
      hlsRef.current = hls;
      hls.loadSource(src); // Using src instead of url
      hls.attachMedia(video);

      hls.on(Hls.Events.MANIFEST_PARSED, (event, data) => {
        // Extract available quality levels
        const availableQualities = data.levels.map((level, index) => ({
          label: `${level.height}p`, // e.g., "480p", "720p", "1080p"
          value: index
        }));
        
        setQualities(availableQualities);
        
        // Set default quality (highest)
        if (availableQualities.length > 0) {
          const autoQuality = -1; // Auto quality
          hls.startLevel = autoQuality;
          setCurrentQuality(autoQuality);
        }
      });

      hls.on(Hls.Events.LEVEL_SWITCHED, (event, data) => {
        const level = data.level;
        setCurrentQuality(level);
        
        if (onQualityChange && hls.levels[level]) {
          onQualityChange(hls.levels[level].height + 'p');
        }
      });

      hls.on(Hls.Events.ERROR, (event, data) => {
        console.error('HLS.js error:', data);
        
        if (data.fatal) {
          switch (data.type) {
            case Hls.ErrorTypes.NETWORK_ERROR:
              console.log('Network error, trying to recover...');
              hls.startLoad();
              break;
            case Hls.ErrorTypes.MEDIA_ERROR:
              console.log('Media error, trying to recover...');
              hls.recoverMediaError();
              break;
            default:
              console.error('Fatal error, cannot recover');
              hls.destroy();
              break;
          }
        }
      });

      return () => {
        hls.destroy();
        hlsRef.current = null;
      };
    } else if (video?.canPlayType('application/vnd.apple.mpegurl')) {
      video.src = src; // Using src instead of url
    }
  }, [src, onQualityChange]); // Updated dependency

  // Function to change video quality
  const handleQualityChange = (qualityIndex: number) => {
    if (hlsRef.current) {
      hlsRef.current.currentLevel = qualityIndex;
    }
  };

  return (
    <div className="video-player-container">
      <ReactPlayer
        ref={playerRef}
        url={src} // Pass src to ReactPlayer's url prop
        controls={props.controls !== undefined ? props.controls : true}
        width={props.width || "100%"}
        height={props.height || "auto"}
        playing={props.playing || false}
        config={{
          file: {
            attributes: {
              crossOrigin: 'anonymous',
              controlsList: 'nodownload',
            },
            tracks: tracks || [],
          },
        }}
        {...props}
      />
      
      {qualities.length === -2 && (
        <div className="quality-selector">
          <select 
            value={currentQuality !== null ? currentQuality : -1}
            onChange={(e) => handleQualityChange(Number(e.target.value))}
          >
            <option value={-1}>Auto</option>
            {qualities.map((quality) => (
              <option key={quality.value} value={quality.value}>
                {quality.label}
              </option>
            ))}
          </select>
        </div>
      )}
    </div>
  );
};

export default EncryptedHLSPlayer;
